import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  Typography,
  useMediaQuery,
  Badge
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "../../ThemeContext/ThemeContext.js";
import "reactjs-popup/dist/index.css";
import Toolbar from "@mui/material/Toolbar";
import { styled } from "@mui/material/styles";
import Avatar from "./Avatar/Avatar";
import FluxVisionLogo from "../../assets/fluxvision_logo.svg";
import fluxvision_logo_light from "../../assets/fluxvision_logo_light.png";
import { useHistory, useLocation } from "react-router-dom";
import IndicatorIcon from "../../assets/Indicator.svg";
import IndicatorIconDarkMode from "../../assets/indicator_dark_mode.svg";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";

import MenuItem from "@mui/material/MenuItem";

import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import {
  StyledMenu,
  StyledMenuItem,
} from "../StyledComponents/Menu/Menu.tsx";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { StyledIconButton } from "../StyledComponents/Buttons/AuradineButtons";
import Navigationcontextprovider from "./context/Navigationcontextprovider.js";
import useNavigationContext from "./context/useNavigationContext";
import { GetUserTechsupport } from "../../api/api.js";
import { SchoolOutlined } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import RolePermissionsDialog from "../rolePermissionsDialog.js";
import ActiveSiteCurtailmentDialog from "../ActiveSiteCurtailment/ActiveSiteCurtailment.js";
import { UseGetActiveCurtailmentSitesList } from "../../api/queries/useGetActiveCurtailSites.ts";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";

const UnselectedRailingItemText = styled(Typography)(({ colors }) => ({
  color: colors?.text?.label,
  fontSize: "14px",
  fontStyle: "normal",
  fontFamily: "Roboto",
  fontWeight: 500,
  lineHeight: "20px",
}));

const SelectedRailingItemText = styled(Typography)(({ colors }) => ({
  color: colors?.primary?.main,
  fontSize: "14px",
  fontStyle: "normal",
  fontFamily: "Roboto",
  fontWeight: 500,
  lineHeight: "20px",
}));

const NavigationRailingStyledMenu = styled(Menu)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem",
    marginTop: "0.80%",
    padding: "0.75rem",
    minWidth: "21rem",
    backgroundColor: colors?.background?.popover,
  },
}));

const NavigationRailingStyledMenuItem = styled(MenuItem)(({ colors }) => ({
  padding: "1rem 1.5rem",
  color: colors?.text?.label,
  /* material-theme/label/large */
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "1.25rem" /* 142.857% */,
  letterSpacing: "0.00625rem",
  backgroundColor: "transparent",
  "&:hover": {
    borderRadius: "6.25rem",
    backgroundColor: colors?.hover?.muted,
  },
  "&.Mui-focusVisible": {
    outline: "none",
    borderRadius: "6.25rem",
    backgroundColor: colors?.hover?.muted,
  },
  "&.Mui-selected": {
    borderRadius: "6.25rem",
    backgroundColor: colors?.primary?.light,
  },
}));

const CustomBadge = styled(Badge)(({ colors }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colors?.text?.primary,
    color: colors?.primary?.contrastText,
    fontSize: "0.55rem",
    minWidth: 16,
    height: 16,
    padding: "0.25rem",
    top: 12,
    right: -2,
  },
}));

const NavigationRailingItemText = React.memo((props) => {
  const {
    setSelectedRouteIndex,
    currentIndex,
    route,
    selectedRouteIndex,
  } = props;
  const { colors } = useTheme();

  return (
    <>
      {selectedRouteIndex === currentIndex ? (
        <>
          <SelectedRailingItemText colors={colors}>
            {route.name}
          </SelectedRailingItemText>
        </>
      ) : (
        <>
          <UnselectedRailingItemText colors={colors}>
            {route.name}
          </UnselectedRailingItemText>
        </>
      )}
    </>
  );
});

const NavigationRailingItem = (props) => {
  const {
    route,
    selectedRouteIndex,
    setSelectedRouteIndex,
    currentIndex,
    setMenuSelectedValue,
    menuSelectedValue,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;
  const [anchorEl, setAnchorEl] = useState(null);
  const { colors, currentTheme } = useTheme();

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const shouldOpenMenu = useMemo(
    () => Boolean(anchorEl),
    [anchorEl]
  );
  const onRailingTextSelect = useCallback(
    (event) => {
      if (
        selectedRouteIndex !== currentIndex &&
        event.type === "click"
      ) {
        setSelectedRouteIndex(currentIndex);
        setMenuSelectedValue(null);

        history.push(route.navigateTo);
      }
    },
    [
      currentIndex,
      history,
      route.navigateTo,
      selectedRouteIndex,
    ]
  );
  const onMenuItemSelect = useCallback(
    (event, index, route) => {
      event.stopPropagation();

      if (event.type === "click") {
        setSelectedRouteIndex(currentIndex);
        setMenuSelectedValue((prev) => {
          if (prev !== index) return index;
          return prev;
        });
        history.push(route);
        handleClose();
      }
    },
    [currentIndex, history, setSelectedRouteIndex]
  );

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // two types of navigationRailingItemText -> one is when there is childrenRoutes and one is when there is no childrenRoutes
  // if there are childrenRoutes, then the navigationRailingItemText will be a dropdown
  // if there are no childrenRoutes, then the navigationRailingItemText will be a simple text
  // on click should navigate when there is no childrenRoutes
  // on click should open the dropdown when there are childrenRoutes and on click of the dropdown item, should navigate to the respective route and still mark the parent.
  return (
    <>
      {!route.hasChildrenRoutes ? (
        <Box
          display={"flex"}
          className={"navigation-railing-item-text"}
          flexDirection={"column"}
          justifyContent={
            selectedRouteIndex === currentIndex
              ? "flex-end"
              : "center"
          }
          alignItems={"center"}
          height={"100%"}
          sx={{ cursor: "pointer" }}
          onClick={(event) => onRailingTextSelect(event)}
        >
          <Box
            className="navigation-railing-item-text"
            display={"flex"}
            padding={"0 1rem "}
          >
            <NavigationRailingItemText {...props} />
          </Box>
          {selectedRouteIndex === currentIndex ? (
            <img src={currentTheme ? currentTheme === "light" ? IndicatorIcon : IndicatorIconDarkMode : IndicatorIcon} />
          ) : (
            <></>
          )}
        </Box>
      ) : (
        <Box
          display={"flex"}
          flexDirection={"column"}
          className={"navigation-railing-item-menu"}
          justifyContent={
            selectedRouteIndex === currentIndex
              ? "flex-end"
              : "center"
          }
          alignItems={
            selectedRouteIndex === currentIndex
              ? "flex-start"
              : "center"
          }
          flexShrink={0}
          sx={{ cursor: "pointer" }}
        >
          <Box
            className={`navigation-railing-item-menu${currentIndex}`}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            onClick={handleMenuOpen}
          >
            <Box
              className="navigation-railing-item-text"
              display={"flex"}
              padding={"0 1rem"}
            >
              <NavigationRailingItemText
                className={`navigation-railing-item-text${currentIndex}`}
                {...props}
              />
            </Box>
            {!Boolean(anchorEl) ? (
              <ArrowDropDownRoundedIcon
                fontSize={"small"}
                sx={{
                  color: colors?.text?.muted,
                }}
              />
            ) : (
              <ArrowDropUpRoundedIcon fontSize={"small"}
                sx={{
                  color: colors?.text?.muted,
                }}
              />
            )}
          </Box>
          {selectedRouteIndex === currentIndex ? (
            <Box
              display={"flex"}
              alignItems={"center"}
              ml={selectedRouteIndex === 2 ? "25%" : "15%"}
            >
              {" "}
              <img src={currentTheme ? currentTheme === "light" ? IndicatorIcon : IndicatorIconDarkMode : IndicatorIcon} />{" "}
            </Box>
          ) : (
            <></>
          )}
          <NavigationRailingStyledMenu
            colors={colors}
            defaultValue={menuSelectedValue}
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            open={shouldOpenMenu}
            onClose={handleClose}
          >
            {route.childrenRoutes.map(
              (childRoute, index) => {
                return (
                  <NavigationRailingStyledMenuItem
                    colors={colors}
                    key={index}
                    selected={
                      menuSelectedValue === index &&
                      childRoute.navigateTo === currentPath
                    }
                    onClick={(event) =>
                      onMenuItemSelect(
                        event,
                        index,
                        childRoute.navigateTo
                      )
                    }
                  >
                    {" "}
                    {childRoute.name}
                  </NavigationRailingStyledMenuItem>
                );
              }
            )}
          </NavigationRailingStyledMenu>
        </Box>
      )}
    </>
  );
};

const NavigationRailing = (props) => {
  const { routes } = props;
  const location = useHistory();
  const { pathname } = useLocation();
  const {
    menuSelectedValue,
    setMenuSelectedValue,
    selectedRouteIndex,
    setSelectedRouteIndex,
  } = useNavigationContext();

  useEffect(() => {
    const currentRoute = routes.find((route) =>
      !route?.hasChildrenRoutes
        ? route?.navigateTo === pathname
        : route?.childrenRoutes.some(
            (childRoute) =>
              childRoute?.navigateTo === pathname
          )
    );

    if (currentRoute?.hasChildrenRoutes) {
      const currentChildRoute =
        currentRoute.childrenRoutes.findIndex(
          (childRoute) => childRoute.navigateTo === pathname
        );
      if (currentChildRoute !== -1) {
        setMenuSelectedValue(currentChildRoute);
      }
    }

    const currentRouteIndex = routes.indexOf(currentRoute);

    if (currentRouteIndex !== -1) {
      setSelectedRouteIndex(currentRouteIndex);
    }
    if (currentRouteIndex === -1) {
      setSelectedRouteIndex(-1);
    }
  }, [routes]);

  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      gap={2}
      height={"100%"}
      className={"navigation-railing"}
    >
      {routes.map((route, index) => {
        return (
          <NavigationRailingItem
            key={index}
            currentIndex={index}
            route={route}
            selectedRouteIndex={selectedRouteIndex}
            setSelectedRouteIndex={setSelectedRouteIndex}
            menuSelectedValue={menuSelectedValue}
            setMenuSelectedValue={setMenuSelectedValue}
          />
        );
      })}
    </Box>
  );
};

const NavBarLeftContainer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [techSupport, setTechSupport] =
    React.useState(false);
  const history = useHistory();
  const { currentTheme } = useTheme();
  console.log(currentTheme);

  useEffect(() => {
    const fn = async () => {
      const token = await getAccessTokenSilently();
      await GetUserTechsupport(token, (data) => {
        if (data.status === "error") {
          setTechSupport(false);
        } else {
          setTechSupport(true);
        }
      });
    };
    fn();
  }, [getAccessTokenSilently]);

  const routes = [
    {
      name: "Home",
      value: "home",
      navigateTo: "/",
      hasChildrenRoutes: false,
    },
    {
      name: "Miner",
      value: "miner",
      hasChildrenRoutes: true,
      childrenRoutes: [
        {
          name: "Miner Inventory",
          value: "minersandgroups",
          navigateTo: "/minerstatus",
        },
        {
          name: "Job Results",
          value: "jobs",
          navigateTo: "/jobresults",
        },
        {
          name: "Site and Group Management",
          value: "siteandgroup",
          navigateTo: "/sitegroupmanagement",
        },
        {
          name: "Tag Management",
          value: "tagmanagement",
          navigateTo: "/tagmanagement",
        },
      ],
    },
    {
      name: "Configuration",
      value: "configuration",
      hasChildrenRoutes: true,
      childrenRoutes: [
        {
          name: "Pools",
          value: "pools",
          navigateTo: "/pools",
        },
        {
          name: "Price Profile",
          value: "priceprofile",
          navigateTo: "/priceConfig",
        },
        {
          name: "Site Demand",
          value: "siteDemand",
          navigateTo: "/demandResponse",
        },
      ],
    },
    {
      name: "Case Management",
      value: "caseManagement",
      navigateTo: "/caseManagement",
      hasChildrenRoutes: false,
    },
  ];

  const routesTechSupport = [
    {
      name: "Home",
      value: "home",
      navigateTo: "/",
      hasChildrenRoutes: false,
    },
    {
      name: "Miner",
      value: "miner",
      hasChildrenRoutes: true,
      childrenRoutes: [
        {
          name: "Miner Inventory",
          value: "minersandgroups",
          navigateTo: "/minerstatus",
        },
        {
          name: "Job Results",
          value: "jobs",
          navigateTo: "/jobresults",
        },
        {
          name: "Site and Group Management",
          value: "siteandgroup",
          navigateTo: "/sitegroupmanagement",
        },
        {
          name: "Tag Management",
          value: "tagmanagement",
          navigateTo: "/tagmanagement",
        },
      ],
    },
    {
      name: "Configuration",
      value: "configuration",
      hasChildrenRoutes: true,
      childrenRoutes: [
        {
          name: "Pools",
          value: "pools",
          navigateTo: "/pools",
        },
        {
          name: "Price Profile",
          value: "priceprofile",
          navigateTo: "/priceConfig",
        },
        {
          name: "Site Demand",
          value: "siteDemand",
          navigateTo: "/demandResponse",
        },
      ],
    },
    {
      name: "Case Management",
      value: "caseManagement",
      navigateTo: "/caseManagement",
      hasChildrenRoutes: false,
    },
    {
      name: "Support",
      value: "support",
      hasChildrenRoutes: true,
      childrenRoutes: [
        {
          name: "Miner Inventory",
          value: "csMinerInventory",
          navigateTo: "/techsupportminerstatus",
        },
        {
          name: "Onboard Operations",
          value: "csOnboardOperations",
          navigateTo: "/techsupportonboard",
        },
        {
          name: "Dashboard",
          value: "csDashboard",
          navigateTo: "/techSupportHome",
        },
      ],
    },
  ];

  return (
    <Box
      display={"flex"}
      flexGrow={1}
      flexDirection={"row"}
      height={"48px !important"}
      gap={8}
      alignItems={"center"}
    >
      <Box
        onClick={() => history.push("/")}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <img
          style={{ cursor: "pointer" }}
          src={currentTheme ? currentTheme === "dark" ? fluxvision_logo_light : FluxVisionLogo : FluxVisionLogo}
          height="24"
          alt="logo"
        />
      </Box>
      <NavigationRailing
        routes={!techSupport ? routes : routesTechSupport}
      />
    </Box>
  );
};

const ActiveSiteCurtailMenu = () => {
  const {
    activeSiteCurtailOpen,
    setActiveSiteCurtailOpen,
    activeSiteCurtailCount,
    setActiveSiteCurtailCount,
  } = useAppContextProvider();
  const { colors } = useTheme();

  const {
    data,
    isFetched,
    refetch,
    isLoading,
  } = UseGetActiveCurtailmentSitesList({
    isOpen: true,
    refetchInterval: !activeSiteCurtailOpen ? 180000 : false,
  });

  const tableData = data?.activeCurtailSchedule?.length
    ? data.activeCurtailSchedule
    : [];

  const count = data?.activeCurtailSchedule?.length || 0;

  useEffect(() => {
    if (isFetched) {
      setActiveSiteCurtailCount(count);
    }
  }, [data, isFetched]);

  if (!isFetched || isLoading || count === 0) {
    return null;
  }

  return (
    <>
      <CustomBadge colors={colors} badgeContent={activeSiteCurtailCount} max={1000000}>
        <Tooltip title="Active Power Curtailment Sites" arrow>
          <StyledIconButton
            colors={colors}
            onClick={async () => {
              await refetch();
              setActiveSiteCurtailOpen(true)
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: '#ff9d00', fontSize: "1.5rem" }}
            >
              energy_savings_leaf
            </span>
          </StyledIconButton>
        </Tooltip>
      </CustomBadge>
      <ActiveSiteCurtailmentDialog
        activeSiteData={tableData}
        isLoading={isLoading}
      />
    </>
  );
};

const NotificationsMenu = () => {
  const history = useHistory();
  const { setSelectedRouteIndex, setMenuSelectedValue } =
    useNavigationContext();
  const { colors } = useTheme();

  const navigateToNotifications = () => {
    history.push("/notifications");
    setSelectedRouteIndex(-1);
  };
  return (
    <>
    <Tooltip title="Notifications" arrow>
        <StyledIconButton colors={colors} onClick={navigateToNotifications}>
          <span
            className="material-symbols-outlined"
            style={{
              color: colors?.text?.muted,
              fontSize: "1.5rem !important",
            }}
          >
            notifications
          </span>
        </StyledIconButton>
      </Tooltip>
    </>
  )
}

const HelpMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const history = useHistory();
  const open = Boolean(anchorEl);
  const { colors } = useTheme();
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { setSelectedRouteIndex, setMenuSelectedValue, rolePermissionsDialogOpen, setRolePersmissionsDialogOpen} =
    useNavigationContext();
  async function downloadPdf(pdfName) {
    try {
      let response = await fetch(encodeURI(`/${pdfName}`));
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = pdfName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  }

  const handlePDFDownload = () => {
    // Replace these URLs with the actual URLs of your PDFs

    const pdfNames = [
      "Teraflux_API_Reference.pdf",
      "Teraflux Miner Hardware Reference 2025-03.pdf",
      "Teraflux Miner Hardware Service Guide 2025-03.pdf",
      "Teraflux Miner Quick Start & Web Interface Reference 2025-07.pdf",
      "Auradine FluxVision 2.0 Release Note.pdf",
      "Teraflux FluxOS Firmware Upgrade 2025-06.pdf",
    ];

    // Download each PDF
    pdfNames.forEach((name) => {
      downloadPdf(name);
    });
  };
  const navigateToFluxGPT = () => {
    setMenuSelectedValue(null);
    setSelectedRouteIndex(-1);
    handleClose();
  };

  const handleTutorialLink = () => {
    window.open(
      "https://docs.google.com/document/d/1fDxoONK589B0VKchLb04u0z-LMEgaQp15dfT4fWLtf4/edit?usp=sharing",
      "_blank"
    );
    setMenuSelectedValue(null);
    setSelectedRouteIndex(-1);
    handleClose();
  };

  const handleRolesByPermissionLink = () => {
    setRolePersmissionsDialogOpen(true)
    setMenuSelectedValue(null);
    setSelectedRouteIndex(-1);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Help" arrow>
        <StyledIconButton
          colors={colors}
          aria-label="more"
          id="long-button"
          aria-controls={open ? "long-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <HelpOutlineRoundedIcon fontSize={"medium"}
            sx={{
              color: colors?.text?.muted,
            }}
          />
        </StyledIconButton>
      </Tooltip>
      <StyledMenu
        colors={colors}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <StyledMenuItem colors={colors} onClick={handlePDFDownload}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={3}
          >
            Documentation{" "}
            <ListItemIcon>
              <LaunchOutlinedIcon sx={{ color: colors?.text?.muted }} />
            </ListItemIcon>
          </Box>
        </StyledMenuItem>
        <StyledMenuItem colors={colors} onClick={handleTutorialLink}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={3}
          >
            Tutorials{" "}
            <SchoolOutlined
              sx={{
                  color: colors?.text?.muted,
                fontSize: "1.65rem",
              }}
            />
          </Box>
        </StyledMenuItem>
        <StyledMenuItem colors={colors} onClick={handleRolesByPermissionLink}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={3}
          >
            Permissions by Role{" "}
            <ListItemIcon>
              <AdminPanelSettingsOutlinedIcon sx={{ color: colors?.text?.muted }} />
            </ListItemIcon>
          </Box>
        </StyledMenuItem>
      </StyledMenu>
      <RolePermissionsDialog
        name="permissions-by-role"
        title="Permissions By Role"
        page="../../FluxVisionPermissionsByRole.html"
        open={rolePermissionsDialogOpen}
        setopen={setRolePersmissionsDialogOpen}
      />
    </>
  );
};

const SettingsMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const history = useHistory();
  const { setSelectedRouteIndex, setMenuSelectedValue } =
    useNavigationContext();
  const open = Boolean(anchorEl);
  const { colors } = useTheme();
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToAdministrators = () => {
    setMenuSelectedValue(null);
    setSelectedRouteIndex(-1);
    history.push("/organization");
    handleClose();
  };

  return (
    <>
      <Tooltip title="Administrators" arrow>
        <StyledIconButton
          colors={colors}
          aria-label="more"
          id="long-button"
          aria-controls={open ? "long-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={navigateToAdministrators}
        >
          <SettingsRoundedIcon fontSize="medium"
            sx={{
              color: colors?.text?.muted,
            }}
          />
        </StyledIconButton>
      </Tooltip>
      <StyledMenu
        colors={colors}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <StyledMenuItem colors={colors} onClick={navigateToAdministrators}>
          Administrators{" "}
        </StyledMenuItem>
      </StyledMenu>
    </>
  );
};

const NavBarRightContainer = ({ isAuthenticated }) => {
  const rightContainer = () => {
    return (
      <Box display={"flex"} flexDirection={"row"} gap={4}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={2.5}
        >
          <ActiveSiteCurtailMenu />
          <NotificationsMenu />
          <HelpMenu />
          <SettingsMenu />
        </Box>
        {isAuthenticated ? <Avatar /> : <></>}
      </Box>
    );
  };
  return rightContainer();
};
const TopNavigation = () => {
  const { isAuthenticated, getAccessTokenSilently } =
    useAuth0();
  const queryClient = new QueryClient();
  const { colors } = useTheme();

  return (
    <QueryClientProvider client={queryClient}>
    <Navigationcontextprovider>
      <Box
        flexGrow={1}
        sx={{
          backgroundColor: colors?.background?.accent,
          maxHeight: "48px",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <NavBarLeftContainer />
          <NavBarRightContainer
            isAuthenticated={isAuthenticated}
          />
        </Toolbar>
      </Box>
    </Navigationcontextprovider>
    </QueryClientProvider>
  );
};

TopNavigation.propTypes = {
  isMobileSidebarOpen: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
};

export default TopNavigation;
